import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// ── Widget types ──────────────────────────────────────────────
export type WidgetType =
  | 'stats'
  | 'revenue-chart'
  | 'activity-feed'
  | 'quick-notes'
  | 'mini-calendar'
  | 'todo-list';

export interface WidgetConfig {
  id: WidgetType;
  label: string;
  visible: boolean;
}

export interface TodoItem {
  id: string;
  text: string;
  done: boolean;
}

// ── Default widget order ──────────────────────────────────────
const DEFAULT_WIDGETS: WidgetConfig[] = [
  { id: 'stats', label: 'Stats Overview', visible: true },
  { id: 'revenue-chart', label: 'Revenue Chart', visible: true },
  { id: 'activity-feed', label: 'Recent Activity', visible: true },
  { id: 'quick-notes', label: 'Quick Notes', visible: true },
  { id: 'mini-calendar', label: 'Calendar', visible: true },
  { id: 'todo-list', label: 'To-do List', visible: true },
];

// ── Default mobile shortcuts (paths from NAV_ITEMS) ──────────
const DEFAULT_SHORTCUTS: string[] = [
  '/invoices',
  '/quotes',
  '/expenses',
  '/clients',
];

// ── Store interface ───────────────────────────────────────────
interface DashboardState {
  // Sidebar
  sidebarExpanded: boolean;
  toggleSidebar: () => void;
  setSidebarExpanded: (v: boolean) => void;

  // Widgets
  widgets: WidgetConfig[];
  setWidgetVisible: (id: WidgetType, visible: boolean) => void;
  moveWidget: (id: WidgetType, direction: 'up' | 'down') => void;
  resetWidgets: () => void;

  // Quick notes
  notes: string;
  setNotes: (v: string) => void;

  // Todos
  todos: TodoItem[];
  addTodo: (text: string) => void;
  toggleTodo: (id: string) => void;
  deleteTodo: (id: string) => void;

  // Customize panel
  customizeOpen: boolean;
  setCustomizeOpen: (v: boolean) => void;

  // Mobile shortcuts
  shortcuts: string[];
  toggleShortcut: (path: string) => void;
  moveShortcut: (path: string, direction: 'up' | 'down') => void;
  reorderShortcuts: (fromIndex: number, toIndex: number) => void;
  resetShortcuts: () => void;
  shortcutsEditing: boolean;
  setShortcutsEditing: (v: boolean) => void;
}

// ── Store ─────────────────────────────────────────────────────
export const useDashboardStore = create<DashboardState>()(
  persist(
    (set, get) => ({
      // Sidebar
      sidebarExpanded: true,
      toggleSidebar: () =>
        set((s) => ({ sidebarExpanded: !s.sidebarExpanded })),
      setSidebarExpanded: (v) => set({ sidebarExpanded: v }),

      // Widgets
      widgets: DEFAULT_WIDGETS,
      setWidgetVisible: (id, visible) =>
        set((s) => ({
          widgets: s.widgets.map((w) =>
            w.id === id ? { ...w, visible } : w,
          ),
        })),
      moveWidget: (id, direction) => {
        const { widgets } = get();
        const idx = widgets.findIndex((w) => w.id === id);
        if (idx < 0) return;
        const target = direction === 'up' ? idx - 1 : idx + 1;
        if (target < 0 || target >= widgets.length) return;
        const next = [...widgets];
        [next[idx], next[target]] = [next[target], next[idx]];
        set({ widgets: next });
      },
      resetWidgets: () => set({ widgets: DEFAULT_WIDGETS }),

      // Quick notes
      notes: '',
      setNotes: (v) => set({ notes: v }),

      // Todos
      todos: [
        { id: '1', text: 'Review Q1 invoices', done: false },
        { id: '2', text: 'Send VAT report', done: true },
        { id: '3', text: 'Follow-up client payments', done: false },
      ],
      addTodo: (text) =>
        set((s) => ({
          todos: [
            ...s.todos,
            { id: Date.now().toString(), text, done: false },
          ],
        })),
      toggleTodo: (id) =>
        set((s) => ({
          todos: s.todos.map((t) =>
            t.id === id ? { ...t, done: !t.done } : t,
          ),
        })),
      deleteTodo: (id) =>
        set((s) => ({ todos: s.todos.filter((t) => t.id !== id) })),

      // Customize panel
      customizeOpen: false,
      setCustomizeOpen: (v) => set({ customizeOpen: v }),

      // Mobile shortcuts
      shortcuts: DEFAULT_SHORTCUTS,
      toggleShortcut: (path) =>
        set((s) => {
          if (s.shortcuts.includes(path)) {
            return { shortcuts: s.shortcuts.filter((p) => p !== path) };
          }
          return { shortcuts: [...s.shortcuts, path] };
        }),
      moveShortcut: (path, direction) => {
        const { shortcuts } = get();
        const idx = shortcuts.indexOf(path);
        if (idx < 0) return;
        const target = direction === 'up' ? idx - 1 : idx + 1;
        if (target < 0 || target >= shortcuts.length) return;
        const next = [...shortcuts];
        [next[idx], next[target]] = [next[target], next[idx]];
        set({ shortcuts: next });
      },
      reorderShortcuts: (fromIndex, toIndex) => {
        const { shortcuts } = get();
        if (fromIndex < 0 || toIndex < 0 || fromIndex >= shortcuts.length || toIndex >= shortcuts.length) return;
        const next = [...shortcuts];
        const [moved] = next.splice(fromIndex, 1);
        next.splice(toIndex, 0, moved);
        set({ shortcuts: next });
      },
      resetShortcuts: () => set({ shortcuts: DEFAULT_SHORTCUTS }),
      shortcutsEditing: false,
      setShortcutsEditing: (v) => set({ shortcutsEditing: v }),
    }),
    {
      name: 'aw-dashboard',
      partialize: (state) => ({
        sidebarExpanded: state.sidebarExpanded,
        widgets: state.widgets,
        notes: state.notes,
        todos: state.todos,
        shortcuts: state.shortcuts,
      }),
    },
  ),
);
